// Career Coach agent — warm, narrative persona for storytelling about
// Thanh Nguyen's career arc. Slash commands run locally against
// resumeData; free-text questions fall through to Gemini using
// `systemPrompt` below as the grounding context.

import type {
  AgentConfig,
  SlashCommand,
  SlashCommandResult,
} from '../AgentConfig';
import { resumeData } from '../../../../data/resumeData';

// ---------------------------------------------------------------------------
// Role slug → resume experience index
// ---------------------------------------------------------------------------
//
// resumeData.experience is order-stable (most recent → oldest):
//   0: MODEC                       — Senior QC Engineer (current)
//   1: Terralogic Project Leader   — May 2025 → Feb 2026
//   2: Terralogic Senior QC        — Nov 2022 → May 2023
//   3: Cisco (On-site, via TL)     — Apr 2024 → Jun 2024
//   4: TMA Solutions               — Aug 2020 → Sep 2022
//   5: IMT Solutions (intern)      — Jun 2020 → Aug 2020
//   6: AG Trade & Investment       — Mar 2016 → Feb 2020
//   7: DongA Bank                  — Oct 2014 → Nov 2015
const ROLE_INDEX: Record<string, number> = {
  modec: 0,
  'terralogic-lead': 1,
  'terralogic-sr-qc': 2,
  cisco: 3,
  tma: 4,
  imt: 5,
  'ag-trade': 6,
  'donga-bank': 7,
};

const ROLE_SLUGS = Object.keys(ROLE_INDEX);

// ---------------------------------------------------------------------------
// Build the timeline block we embed in the system prompt. This is what makes
// Gemini answer with grounded facts instead of hallucinating.
// ---------------------------------------------------------------------------
function buildResumeContext(): string {
  const lines: string[] = [];
  lines.push('CAREER TIMELINE (most recent first):');
  for (const exp of resumeData.experience) {
    lines.push(`- ${exp.duration} | ${exp.position} @ ${exp.company}`);
    for (const r of exp.responsibilities ?? []) {
      lines.push(`    - ${r}`);
    }
    for (const p of exp.projects ?? []) {
      lines.push(`    * ${p.title}`);
      for (const d of p.description ?? []) {
        lines.push(`        - ${d}`);
      }
    }
  }
  lines.push('');
  lines.push('EDUCATION:');
  for (const e of resumeData.education) {
    lines.push(`- ${e.duration} | ${e.degree} (${e.field}) — ${e.institution}`);
  }
  lines.push('');
  lines.push(`CERTIFICATIONS & AWARDS: ${resumeData.certifications.length} total`);
  for (const c of resumeData.certifications.slice(0, 6)) {
    lines.push(`- ${c.name}${c.issuer ? ` — ${c.issuer}` : ''}`);
  }
  return lines.join('\n');
}

const RESUME_CONTEXT = buildResumeContext();

const SYSTEM_PROMPT = `You are "Career Coach", a warm, professional AI persona introducing Thanh Nguyen (full name: Nguyen Tan Thanh) to recruiters, hiring managers, and curious visitors of his portfolio site.

Thanh is currently the Senior QC Engineer at MODEC (March 2026 → present), where he is the sole QC engineer for an internal AI Chat application — owning the end-to-end quality lifecycle for feature requests from multiple internal teams and Product Owners. Before MODEC he was Project Leader at Terralogic (May 2025 → Feb 2026) leading API automation for Cisco's Catalyst Center, with a deep prior background as a Senior QC Engineer and Test Automation Engineer.

${RESUME_CONTEXT}

PERSONA & TONE
- Speak in the first person about Thanh as a recruiter pitch would: "In 2024 he spent three months on-site in San Jose contributing to Cisco's testbed topology..."
- Warm, narrative, and professional. Frame answers as career story beats.
- You may occasionally use "career story" framing for color, but keep it grounded.

OUTPUT RULES
- 2-4 sentences per answer unless the visitor explicitly asks for depth.
- Cite specific roles, companies, and years (e.g., "at Terralogic in 2025", "during his TMA years 2020-2022").
- NEVER invent facts — companies, dates, projects, or skills not listed in the resume context above. If unsure, say "that's not in the public resume" and pivot to what is.
- Avoid bullet lists unless the question explicitly asks for a list. Prefer flowing prose.
- The visitor is in a narrow terminal pane — keep paragraphs short.
- If the visitor asks something off-topic (weather, politics, jokes, code help), gently redirect to Thanh's career.`;

// ---------------------------------------------------------------------------
// Slash command handlers
// ---------------------------------------------------------------------------

function handleTimeline(): SlashCommandResult {
  const output: string[] = [];
  output.push('Career timeline');
  output.push('───────────────');
  // Walk in reverse (oldest → newest) so the arc reads chronologically.
  const reversed = [...resumeData.experience].reverse();
  for (const exp of reversed) {
    const headline = exp.responsibilities?.[0]
      ?? exp.projects?.[0]?.description?.[0]
      ?? exp.projects?.[0]?.title
      ?? 'Early-career role outside tech.';
    const trimmed = headline.length > 90 ? `${headline.slice(0, 87)}...` : headline;
    output.push(`▌ ${exp.duration}`);
    output.push(`  ${exp.position} @ ${exp.company}`);
    output.push(`  → ${trimmed}`);
    output.push('');
  }
  output.push('Drill into any role with: /role <slug>');
  output.push(`Slugs: ${ROLE_SLUGS.join(', ')}`);
  return { output };
}

function handleRole(rawArgs: string): SlashCommandResult {
  const slug = rawArgs.trim().toLowerCase();
  if (!slug) {
    return {
      output: [
        'Usage: /role <slug>',
        `Valid slugs: ${ROLE_SLUGS.join(', ')}`,
      ],
    };
  }
  const idx = ROLE_INDEX[slug];
  if (idx === undefined) {
    return {
      output: [
        `Unknown role slug: "${slug}"`,
        `Valid slugs: ${ROLE_SLUGS.join(', ')}`,
      ],
    };
  }
  const exp = resumeData.experience[idx];
  const output: string[] = [];
  output.push(`${exp.position}`);
  output.push(`${exp.company} — ${exp.duration}`);
  output.push('─'.repeat(Math.min(40, exp.company.length + exp.duration.length + 3)));
  if (exp.responsibilities && exp.responsibilities.length > 0) {
    output.push('What he did:');
    for (const r of exp.responsibilities) {
      output.push(`  ● ${r}`);
    }
  }
  if (exp.projects && exp.projects.length > 0) {
    output.push('Projects:');
    for (const p of exp.projects) {
      output.push(`  ● ${p.title}`);
      for (const d of p.description ?? []) {
        output.push(`      ${d}`);
      }
    }
  }
  if ((!exp.responsibilities || exp.responsibilities.length === 0)
      && (!exp.projects || exp.projects.length === 0)) {
    output.push('(Pre-tech role — limited public detail on the resume.)');
  }
  return {
    output,
    followUpPrompt: `Tell me more about Thanh's time at ${exp.company}.`,
  };
}

function handleWhyModec(): SlashCommandResult {
  return {
    output: [
      'Why MODEC?',
      '──────────',
      "Thanh's move from Project Leader at Terralogic to sole QC at MODEC",
      'was a deliberate shift from scale-out to deep ownership. At Terralogic he',
      'was orchestrating API automation across Catalyst Center suites and',
      'coordinating contributors; at MODEC he owns the entire quality lifecycle',
      'of an internal AI Chat app end-to-end — strategy, plans, manual and',
      'automation — for requests coming from multiple teams across the company.',
      '',
      'It is the rare combination of greenfield (he is establishing testing',
      'practices from scratch as the first dedicated QC) plus high-stakes AI',
      'product work, which aligns with his recent focus on Applied AI, MCP, and',
      'prompt engineering.',
    ],
  };
}

const slashCommands: SlashCommand[] = [
  {
    name: '/timeline',
    description: 'Full career arc, oldest to newest',
    handler: handleTimeline,
  },
  {
    name: '/role',
    description: 'Drill into one role by slug',
    example: '/role modec',
    handler: handleRole,
  },
  {
    name: '/why-modec',
    description: 'Why he chose his current role at MODEC',
    handler: handleWhyModec,
  },
];

export const careerCoach: AgentConfig = {
  id: 'coach',
  label: 'Career Coach',
  shortLabel: 'coach',
  emoji: '>',
  accentColor: 'primary',
  systemPrompt: SYSTEM_PROMPT,
  welcome: [
    '> Career Coach — telling Thanh\'s story',
    'Type a question, or try a slash command:',
    '  /timeline       full career arc',
    '  /role <slug>    drill into one role  (e.g. /role modec)',
    '  /why-modec      why he chose his current role',
    '  /help           list all commands',
  ],
  slashCommands,
};
